import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccessibilityInfo,
  ColorValue,
  FlatList,
  ImageSourcePropType,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  scrollTo,
  useAnimatedRef,
  useScrollOffset,
  useSharedValue,
} from 'react-native-reanimated';
import PageIndicator from 'shared/components/PageIndicator/PageIndicator';
import { isWeb } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

import { getDragTargetPage } from './InfoCarousel.helpers';
import InfoCarouselHeroImage from './InfoCarouselHeroImage';
import InfoCarouselStepContent from './InfoCarouselStepContent';

const SCROLL_EVENT_THROTTLE = 16;
const HERO_BORDER_RADIUS = 8;
const DRAG_ACTIVATION_DISTANCE = 10;

export interface InfoCarouselStep {
  key: string;
  title: string;
  body: string;
  image: ImageSourcePropType;
  imageBackgroundColor?: ColorValue;
  footer?: ReactNode;
}

interface InfoCarouselProps {
  steps: InfoCarouselStep[];
}

const InfoCarousel = ({ steps }: InfoCarouselProps) => {
  const { t: translatedText } = useTranslation('componentsStrings', {
    keyPrefix: 'infoCarouselStrings',
  });
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [pageWidth, setPageWidth] = useState(windowWidth);
  const [pageHeight, setPageHeight] = useState(windowHeight);
  const [activeStep, setActiveStep] = useState(0);
  const [heroHeight, setHeroHeight] = useState(0);
  const stepListRef = useAnimatedRef<FlatList<InfoCarouselStep>>();
  const scrollOffset = useScrollOffset(stepListRef);
  const dragStartStep = useSharedValue(0);

  const lastStep = steps.length - 1;
  const stepPositionLabel = translatedText('stepPosition', {
    current: activeStep + 1,
    total: steps.length,
  });

  const handleStepAreaLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    setPageWidth(nativeEvent.layout.width);
    setPageHeight(nativeEvent.layout.height);
  };

  const handleIllustrationLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    setHeroHeight(nativeEvent.layout.height);
  };

  const handleScroll = ({
    nativeEvent,
  }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const visibleStep = Math.round(
      nativeEvent.contentOffset.x / nativeEvent.layoutMeasurement.width,
    );
    if (visibleStep === activeStep) {
      return;
    }
    setActiveStep(visibleStep);
    AccessibilityInfo.announceForAccessibility(
      translatedText('stepPosition', {
        current: visibleStep + 1,
        total: steps.length,
      }),
    );
  };

  const mouseDragGesture = Gesture.Pan()
    .enabled(isWeb)
    .activeOffsetX([-DRAG_ACTIVATION_DISTANCE, DRAG_ACTIVATION_DISTANCE])
    .onStart(() => {
      dragStartStep.value = Math.round(scrollOffset.value / pageWidth);
    })
    .onEnd(({ translationX }) => {
      const targetStep = getDragTargetPage({
        startPage: dragStartStep.value,
        translationX,
        pageWidth,
        lastPage: lastStep,
      });
      scrollTo(stepListRef, targetStep * pageWidth, 0, true);
    });

  return (
    <View style={styles.container}>
      <View style={styles.stepArea} onLayout={handleStepAreaLayout}>
        <View style={[styles.heroLayer, { height: heroHeight }]}>
          <View style={styles.heroFrame}>
            {steps.map((step, stepPosition) => (
              <InfoCarouselHeroImage
                key={step.key}
                image={step.image}
                imageBackgroundColor={step.imageBackgroundColor}
                stepPosition={stepPosition}
                pageWidth={pageWidth}
                scrollOffset={scrollOffset}
              />
            ))}
          </View>
        </View>
        <GestureDetector gesture={mouseDragGesture}>
          <Animated.FlatList
            ref={stepListRef}
            testID="info-carousel-steps"
            data={steps}
            keyExtractor={(step) => step.key}
            renderItem={({ item }) => (
              <InfoCarouselStepContent
                title={item.title}
                body={item.body}
                footer={item.footer}
                width={pageWidth}
                height={pageHeight}
                onIllustrationLayout={handleIllustrationLayout}
              />
            )}
            horizontal
            pagingEnabled
            bounces={false}
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={SCROLL_EVENT_THROTTLE}
            onScroll={handleScroll}
            style={styles.stepList}
          />
        </GestureDetector>
      </View>
      <View style={styles.pageIndicator}>
        <PageIndicator
          pageCount={steps.length}
          activePage={activeStep}
          accessibilityLabel={stepPositionLabel}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  heroFrame: {
    borderRadius: HERO_BORDER_RADIUS,
    flex: 1,
    overflow: 'hidden',
  },
  heroLayer: {
    left: 0,
    padding: theme.spacing.m,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  pageIndicator: {
    alignItems: 'center',
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  stepArea: {
    flex: 1,
  },
  stepList: {
    flex: 1,
  },
});

export default InfoCarousel;
